import React, { useEffect, useRef, useState } from 'react'
import { Button, Tooltip } from '@material-ui/core'
import { connect } from 'react-redux'
import FileSearchField from './FileSearchField.js'
import {
  canOpenDocument,
  openDocument,
  constructDocumentParams
} from '../util.js'
import {
  AttachFile,
  TextRotateVertical,
  OpenInBrowser as Open,
  Save,
  SaveAlt,
  Send
} from '@material-ui/icons'
import {
  FOOTER_BACKGROUND,
  FOOTER_HEIGHT
} from '../styles.js'
import {
  isOutlook,
  isOutlookReadMode,
  isOutlookComposeMode
} from '../lib/office.js'
import redux from '../redux/index.js'

// Reneders save attachment button for specifically Outlook Compose Mode
const HasAttachemtComposeModeButton = ({ onSaveAttachmentsClick, isFolder, icon }) => {
  const [isEnabled, setIsEnabled] = useState(false)
  const timersRef = useRef([])

  const refreshAttachmentState = () => {
    probeHasAttachments().then(setIsEnabled).catch(() => setIsEnabled(false))
  }

  const scheduleRefreshes = () => {
    refreshAttachmentState()
    const delays = [150, 500]
    delays.forEach((delay) => {
      const id = setTimeout(refreshAttachmentState, delay)
      timersRef.current.push(id)
    })
  }

  useEffect(() => {
    if (isOutlookComposeMode()) {
      scheduleRefreshes()
      if (Office?.context?.mailbox?.addHandlerAsync && Office?.EventType?.ItemChanged) {
        Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, scheduleRefreshes)
      }
    }

    return () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
      if (Office?.context?.mailbox?.removeHandlerAsync && Office?.EventType?.ItemChanged) {
        Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, { handler: scheduleRefreshes })
      }
    }
  }, [isFolder])

  const isDisabled = !(isFolder && isEnabled)
  return (
    <Button
      disabled={isDisabled}
      onClick={onSaveAttachmentsClick}
      style={actionButtonStyle(isDisabled)}
    >
      {icon || <SaveAlt style={actionIconStyle} />}
    </Button>
  )
}
const Footer = ({
  strings,
  currentPage,
  saveDisabled,
  saveText,
  onShowSaveDialog,
  onSaveAttachmentsClick,
  onShowRenameAttachmentsDialog,
  openDisabled,
  attachDisabled,
  selected,
  selectedFileNode,
  selectedFileNodes,
  sendAndFileDisabled,
  onAttachFile,
  onAttachFiles,
  onSendAndFileClick,
  sharePointSaveEnabled,
  sharePointTemplatesEnabled,
  userData,
  showSaveButton = true,
  showSendAndFileButton = true
}) => {
  const { current, previous } = selected
  const [hasCurrentItemAttachments, setHasCurrentItemAttachments] = useState(false)
  const [isFooterSearchExpanded, setIsFooterSearchExpanded] = useState(false)
  const timersRef = useRef([])

  useEffect(() => {
    const refreshAttachmentState = () => {
      if (!isOutlookReadMode()) {
        setHasCurrentItemAttachments(false)
        return
      }
      probeHasAttachments().then(setHasCurrentItemAttachments).catch(() => setHasCurrentItemAttachments(false))
    }

    const scheduleRefreshes = () => {
      refreshAttachmentState()
      const delays = [150, 500]
      delays.forEach((delay) => {
        const id = setTimeout(refreshAttachmentState, delay)
        timersRef.current.push(id)
      })
    }

    scheduleRefreshes()

    if (isOutlook() && Office?.context?.mailbox?.addHandlerAsync && Office?.EventType?.ItemChanged) {
      Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged, scheduleRefreshes)
      return () => {
        timersRef.current.forEach(clearTimeout)
        timersRef.current = []
        if (Office?.context?.mailbox?.removeHandlerAsync) {
          Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, { handler: scheduleRefreshes })
        }
      }
    }

    return () => {
      timersRef.current.forEach(clearTimeout)
      timersRef.current = []
    }
  }, [])

  const isFolder = selectedFileNode && (selectedFileNode.type === 'folder' || selectedFileNode.type === 'rootfolder')
  const saveAttachmentsDisabled = !(isFolder && hasCurrentItemAttachments)

  const onOpenClick = () => {
    if (selectedFileNode && selectedFileNode.type === 'file') {
      // FIXME clean this up once we remove the template page from add-in,
      // previous remembers the context when we open a file from the template page
      const context = current && current.type !== 'templates' ? current : previous
      const params = constructDocumentParams(userData, context)
      openDocument(selectedFileNode.uri, params)
    }
  }

  const renderFooterSearch = () => {
    const showFooterSearch = sharePointSaveEnabled || currentPage === 'templates'
    if (!showFooterSearch) {
      return null
    }

    return (
      <div style={footerSearchStyle(isFooterSearchExpanded)}>
        <FileSearchField
          inFooter
          footerExpanded={isFooterSearchExpanded}
          onFocusChange={setIsFooterSearchExpanded}
        />
      </div>
    )
  }

  const renderOutlookComposeMode = () => {
    const renderAttachButton = () => {
      const onAttachClick = () => {
        if (Array.isArray(selectedFileNodes) && selectedFileNodes.length > 1) {
          onAttachFiles(selectedFileNodes)
        } else {
          onAttachFile(selectedFileNode)
        }
      }
      if (sharePointSaveEnabled) {
        return (
          <Tooltip title={strings.attach} arrow>
            <span>
              <Button
                disabled={attachDisabled}
                onClick={onAttachClick}
                style={actionButtonStyle(attachDisabled)}
              >
                <AttachFile style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    const renderSendAndFileButton = () => {
      if (sharePointSaveEnabled && showSendAndFileButton) {
        return (
          <Tooltip title={strings.send_and_file} arrow>
            <span>
              <Button
                disabled={sendAndFileDisabled}
                onClick={() => onSendAndFileClick(current, selectedFileNode)}
                style={actionButtonStyle(sendAndFileDisabled)}
              >
                <Send style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }
    const renderSavePdfButton = () => {
      if (sharePointSaveEnabled) {
        return (
          <Tooltip title={strings.save_attachments} arrow>
            <span>
              <HasAttachemtComposeModeButton
                isFolder={selectedFileNode && (selectedFileNode.type === 'folder' || selectedFileNode.type === 'rootfolder')}
                onSaveAttachmentsClick={() => onSaveAttachmentsClick(current, selectedFileNode)}
              />
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    const renderRenameAttachmentsButton = () => {
      if (sharePointSaveEnabled) {
        return (
          <Tooltip title={strings.rename_attachments_dialog_title} arrow>
            <span>
              <HasAttachemtComposeModeButton
                isFolder={selectedFileNode && (selectedFileNode.type === 'folder' || selectedFileNode.type === 'rootfolder')}
                onSaveAttachmentsClick={() => onShowRenameAttachmentsDialog(current, selectedFileNode)}
                icon={<TextRotateVertical style={actionIconStyle} />}
              />
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    return (
      <div style={footerStyle}>
        <div style={footerContentStyle}>
          {renderFooterSearch()}
          {!isFooterSearchExpanded
            ? (
              <div style={footerActionsStyle}>
                {renderSavePdfButton()}
                {renderRenameAttachmentsButton()}
                {renderAttachButton()}
                {renderSendAndFileButton()}
              </div>
              )
            : null}
        </div>
      </div>
    )
  }

  const renderOpenSaveMode = () => {
    const renderSaveAttachmentsButton = () => {
      if (sharePointSaveEnabled && showSaveButton && isOutlookReadMode()) {
        return (
          <Tooltip title={strings.save_attachments} arrow>
            <span>
              <Button
                disabled={saveAttachmentsDisabled}
                onClick={() => onSaveAttachmentsClick(current, selectedFileNode)}
                style={actionButtonStyle(saveAttachmentsDisabled)}
              >
                <SaveAlt style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    const renderRenameAttachmentsButton = () => {
      if (sharePointSaveEnabled && showSaveButton && isOutlookReadMode()) {
        return (
          <Tooltip title={strings.rename_attachments_dialog_title} arrow>
            <span>
              <Button
                disabled={saveAttachmentsDisabled}
                onClick={() => onShowRenameAttachmentsDialog(current, selectedFileNode)}
                style={actionButtonStyle(saveAttachmentsDisabled)}
              >
                <TextRotateVertical style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    const renderSaveButton = () => {
      if (sharePointSaveEnabled && showSaveButton) {
        return (
          <Tooltip title={saveText} arrow>
            <span>
              <Button
                disabled={saveDisabled}
                onClick={onShowSaveDialog}
                style={actionButtonStyle(saveDisabled)}
              >
                <Save style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    const renderOpenButton = () => {
      if (sharePointSaveEnabled || (sharePointTemplatesEnabled && currentPage === 'templates')) {
        return (
          <Tooltip title={strings.open} arrow>
            <span>
              <Button
                disabled={openDisabled}
                onClick={onOpenClick}
                style={actionButtonStyle(openDisabled)}
              >
                <Open style={actionIconStyle} />
              </Button>
            </span>
          </Tooltip>
        )
      } else {
        return null
      }
    }

    return (
      <div style={footerStyle}>
        <div style={footerContentStyle}>
          {renderFooterSearch()}
          {!isFooterSearchExpanded
            ? (
              <div style={footerActionsStyle}>
                {renderOpenButton()}
                {renderSaveAttachmentsButton()}
                {renderRenameAttachmentsButton()}
                {renderSaveButton()}
              </div>
              )
            : null}
        </div>
      </div>
    )
  }

  if (isOutlookComposeMode()) {
    return renderOutlookComposeMode()
  } else {
    return renderOpenSaveMode()
  }
}

const footerStyle = {
  backgroundColor: FOOTER_BACKGROUND,
  height: FOOTER_HEIGHT
}

const footerContentStyle = {
  display: 'flex',
  alignItems: 'center',
  height: '100%'
}

const footerSearchStyle = (expanded) => {
  if (expanded) {
    return {
      flex: 1,
      minWidth: 0,
      marginLeft: 6,
      marginRight: 8
    }
  }

  return {
    flex: '0 0 34px',
    width: 34,
    marginLeft: 6,
    marginRight: 8
  }
}

const footerActionsStyle = {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  paddingRight: 2
}

export const actionButtonStyle = (disabled = false) => {
  return {
    color: disabled ? '#aaafb2' : 'white',
    marginTop: 0,
    marginRight: '0px',
    minWidth: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle'
  }
}

export const actionIconStyle = {
  marginRight: '5px',
  fontSize: 20,
  width: 20,
  height: 20,
  display: 'block',
  verticalAlign: 'middle'
}

function probeHasAttachments () {
  return new Promise((resolve) => {
    const item = Office?.context?.mailbox?.item
    if (!item) {
      resolve(false)
      return
    }

    if (typeof item.getAttachmentsAsync === 'function') {
      item.getAttachmentsAsync({ asyncContext: { currentItem: item } }, (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          const list = Array.isArray(res.value) ? res.value : []
          resolve(list.some(fileAttachment))
        } else {
          resolve(hasItemAttachments(item))
        }
      })
    } else {
      resolve(hasItemAttachments(item))
    }
  })
}

function hasItemAttachments (item) {
  const list = Array.isArray(item?.attachments) ? item.attachments : []
  return list.some(fileAttachment)
}

function fileAttachment (a) {
  const type = String(a?.attachmentType || '').toLowerCase()
  const isInline = a?.isInline === true
  return !isInline && (type === 'file' || type.length === 0)
}

const mapStateToProps = (state) => {
  const { strings } = state.locale
  const { selectedFileNode } = state.files
  const selectedFileNodes = Array.isArray(state.files.selectedFileNodes) ? state.files.selectedFileNodes : []
  const isFile = selectedFileNode && selectedFileNode.type === 'file'
  const isFolder = selectedFileNode && (selectedFileNode.type === 'folder' || selectedFileNode.type === 'rootfolder')
  const hasSelectedFiles = selectedFileNodes.length > 0
  const saveText = isFile ? strings.save_as : strings.save
  const canOpen = isFile && canOpenDocument(selectedFileNode.uri)
  const selectedItem = state.selected.current
  return {
    strings,
    currentPage: state.page,
    saveDisabled: !(selectedItem && selectedItem.url),
    saveText,
    openDisabled: !canOpen,
    attachDisabled: isOutlookComposeMode() ? !hasSelectedFiles : !isFile,
    selected: state.selected,
    selectedFileNode,
    selectedFileNodes,
    sendAndFileDisabled: !isFolder,
    sharePointSaveEnabled: state.login.userData.sharePointSaveEnabled,
    sharePointTemplatesEnabled: state.login.userData.sharePointTemplatesEnabled,
    userData: state.login.userData
  }
}

const mapDispatchToProps = (dispatch) => {
  const { dialog, files, save } = redux.actions
  return {
    onSendAndFileClick: (siteItem, fileItem) => {
      dispatch(save.sendAndFile(siteItem, fileItem))
    },
    onShowSaveDialog: () => {
      dispatch(save.readDocumentProperties())
      dispatch(dialog.showSaveDialog())
    },
    onAttachFile: (selectedFileNode) => {
      dispatch(files.attachFile(selectedFileNode))
    },
    onAttachFiles: (selectedFileNodes) => {
      dispatch(files.attachFiles(selectedFileNodes))
    },
    onSaveAttachmentsClick: (siteItem, fileItem) => {
      dispatch(save.uploadFileAttachments(siteItem, fileItem, () => {
        const { type } = fileItem
        if (type === 'rootfolder') {
          dispatch(files.getRootFolder(siteItem))
        } else if (type === 'folder') {
          dispatch(files.getSubFolder(siteItem, fileItem))
        }
      }))
    },
    onShowRenameAttachmentsDialog: (siteItem, fileItem) => {
      dispatch(dialog.showRenameAttachmentsDialog(siteItem, fileItem))
    }
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Footer)
